// @ts-check
// Comandos da LGPD não consentida (2.0.0, contrato 09 de campanhas base, seções 6.9 e 12): record.marketingOptOut
// (pessoa, ou contato principal do card) e record.marketingConsent (pessoa marcada, com SECURITY). O registro é do
// passo 0 (client/src/lib/command-registry.js); aqui ficam o que a ficha diz ao registro e a execução, que abre os
// mesmos diálogos da linha "Comunicações de marketing".
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchMarketingStatus } from '../../data/marketing-consent.js';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import { openMarketingConsentDialog } from './marketing-consent-row.js';

/** Comandos da LGPD já prontos para aparecer. @type {ReadonlySet<string>} */
export const MARKETING_CONSENT_COMMAND_IDS = new Set(['record.marketingOptOut', 'record.marketingConsent']);

const MARK_FIELD = 'marketingOptOut';

/** O papel lê a pessoa e o campo da marca (sem ele, o servidor recusa e a linha some). */
function canReadPersonMark() {
  const personObject = objectsByNameSingular.value.get('person');
  return Boolean(personObject && canReadObjectRecords(personObject) && personObject.fields.some((field) => field.name === MARK_FIELD));
}

/**
 * O que o registro de comandos precisa saber da ficha aberta (CommandRecord): a marca da pessoa, o espelho do contato
 * principal no card e se o papel pode marcar. Marcar pede só a leitura da pessoa e do campo (o servidor confere o escopo).
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata @param {Record<string, any>} record
 * @returns {{ marketingOptOut?: boolean, contactMarketingOptOut?: boolean, pointOfContactId?: string | null, canMarkMarketingOptOut?: boolean }}
 */
export function describeRecordMarketingConsent(objectMetadata, record) {
  if (!record) return {};
  if (objectMetadata.nameSingular === 'person') {
    return { marketingOptOut: record.marketingOptOut === true, canMarkMarketingOptOut: !record.anonymizedAt && canReadPersonMark() };
  }
  if (objectMetadata.nameSingular === 'enrollmentOpportunity') {
    return {
      contactMarketingOptOut: record.contactMarketingOptOut === true,
      pointOfContactId: record.pointOfContactId ?? null,
      canMarkMarketingOptOut: Boolean(record.pointOfContactId) && canReadPersonMark(),
    };
  }
  return {};
}

/** @param {string} objectNameSingular @param {Record<string, any>} record */
function personOf(objectNameSingular, record) {
  const personObject = objectsByNameSingular.value.get('person');
  if (objectNameSingular === 'person') return { personId: String(record.id), label: getRecordLabel(personObject, record) };
  return { personId: String(record.pointOfContactId ?? ''), label: getRecordLabel(personObject, record.pointOfContact) };
}

/**
 * Executa um comando da LGPD. Devolve false quando o comando não é desta área.
 * @param {import('../../lib/command-registry.js').CommandDescriptor} command
 * @param {import('../command-menu/command-actions.js').CommandRuntime} runtime
 * @returns {Promise<boolean>}
 */
export async function runMarketingConsentCommand(command, runtime) {
  if (!MARKETING_CONSENT_COMMAND_IDS.has(command.id)) return false;
  const target = runtime.record;
  if (!target?.record) return true;
  const { personId, label } = personOf(target.objectMetadata.nameSingular, target.record);
  if (!personId) return true;
  const onDone = () => target.reload();
  if (command.id === 'record.marketingOptOut') {
    openMarketingConsentDialog({ kind: 'mark', personId, personLabel: label || t('record.untitled'), isMinor: false, onDone });
    return true;
  }
  // o consentimento de menor pede o responsável: a situação diz se a pessoa é menor
  try {
    const status = await fetchMarketingStatus(personId);
    openMarketingConsentDialog({ kind: 'register', personId, personLabel: label || t('record.untitled'), isMinor: status.requiresGuardian === true, onDone });
  } catch (error) {
    showErrorSnackbar(error);
  }
  return true;
}
